import React, { useEffect, useState } from 'react';
import { BookOpen, CalendarClock, Mic } from 'lucide-react';
import { getRecentActivity, getUserOverview } from '../../api/userApi';
import { capitalizeText } from '../../utils/capitalizeText';
import Badge1Meeting from '../../assets/icons/meeting-badges/1-meeting.svg';
import Badge5Meeting from '../../assets/icons/meeting-badges/5-meeting.svg';
import Badge15Meeting from '../../assets/icons/meeting-badges/15-meeting.svg';
import Badge30Meeting from '../../assets/icons/meeting-badges/30-meeting.svg';
import Badge40Meeting from '../../assets/icons/meeting-badges/40-meeting.svg';
import Badge50Meeting from '../../assets/icons/meeting-badges/50-meeting.svg';
import LockBadge from '../../assets/icons/meeting-badges/lock.svg';
import StreakSummary from '../../components/Streak/StreakSummary';

const BADGE_IMAGES = {
  1: Badge1Meeting,
  5: Badge5Meeting,
  15: Badge15Meeting,
  30: Badge30Meeting,
  40: Badge40Meeting,
  50: Badge50Meeting,
};

function activityTitle(item) {
  return capitalizeText(item?.meeting?.title || item?.training?.title || 'Activity');
}

function ActivityIcon({ type }) {
  if (type === 'meeting') return <Mic size={18} />;
  if (type === 'training') return <BookOpen size={18} />;
  return <CalendarClock size={18} />;
}

export default function ProfileOverview({ streak, streakLoading }) {
  const [overview, setOverview] = useState(null);
  const [activity, setActivity] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    Promise.allSettled([
      getUserOverview(),
      getRecentActivity({ limit: 8 }),
    ]).then(([overviewResult, activityResult]) => {
      if (!active) return;

      if (overviewResult.status === 'fulfilled') {
        setOverview(overviewResult.value);
      }
      if (activityResult.status === 'fulfilled') {
        setActivity(activityResult.value?.activity || []);
      }
      setLoading(false);
    });

    return () => {
      active = false;
    };
  }, []);

  const completed = Number(overview?.totalMeetingsCompleted || 0);
  const nextMilestone = overview?.nextMilestone;
  const remaining = nextMilestone ? Math.max(0, nextMilestone - completed) : 0;
  const milestones = Array.isArray(overview?.badgeMilestones) ? overview.badgeMilestones : [];
  const progress = Math.min(100, Math.max(0, Number(overview?.progressPercent || 0)));

  return (
    <div className="up-profile-overview">
      <div className="up-profile-metrics-row">
        <section className="up-streak-card">
          <StreakSummary streak={streak} loading={streakLoading} profile />
        </section>

        {loading ? (
          <div className="up-profile-overview-loading">Loading progress...</div>
        ) : (
          <section className="up-progress-card">
            <div className="up-section-heading">
              <div>
                <h2>Conversations Completed</h2>
                <p>
                  {!overview
                    ? 'Progress is temporarily unavailable'
                    : nextMilestone
                    ? `${remaining} more ${remaining === 1 ? 'conversation' : 'conversations'} to your next badge`
                    : 'All badges unlocked'}
                </p>
              </div>
              {overview && <strong>{completed}</strong>}
            </div>

            {overview && (
              <>
                <div
                  className="up-progress-track"
                  role="progressbar"
                  aria-label="Progress toward next conversation badge"
                  aria-valuemin="0"
                  aria-valuemax="100"
                  aria-valuenow={progress}
                >
                  <span style={{ width: `${progress}%` }} />
                </div>

                <div className="up-badge-grid">
                  {milestones.map((milestone) => {
                    const unlocked = completed >= milestone;
                    return (
                      <div className="up-badge-item" key={milestone}>
                        <img
                          src={unlocked ? BADGE_IMAGES[milestone] : LockBadge}
                          alt={unlocked ? `${milestone} conversation badge` : `${milestone} conversation badge locked`}
                        />
                        <span>{Math.min(completed, milestone)}/{milestone}</span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </section>
        )}
      </div>

      {loading ? (
        <div className="up-profile-overview-loading">Loading activity...</div>
      ) : (
        <section className="up-activity-card">
          <div className="up-section-heading">
            <div>
              <h2>Recent Activity</h2>
              <p>Your latest practice and training sessions</p>
            </div>
          </div>

          {activity?.length ? (
            <ul className="up-activity-list">
              {activity.map((item, index) => (
                <li key={`${item.type}-${item.at}-${index}`}>
                  <span className={`up-activity-icon up-activity-icon--${item.type}`} aria-hidden="true">
                    <ActivityIcon type={item.type} />
                  </span>
                  <div>
                    <strong>{activityTitle(item)}</strong>
                    <span>
                      {new Date(item.at).toLocaleString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          ) : activity ? (
            <div className="up-activity-empty">
              <Mic size={21} aria-hidden="true" />
              <strong>No recent activity yet</strong>
              <span>Complete a practice or training session to see it here.</span>
            </div>
          ) : (
            <div className="up-activity-empty">
              <CalendarClock size={21} aria-hidden="true" />
              <strong>Activity is temporarily unavailable</strong>
              <span>Your profile and billing details are still available.</span>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
